import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouter, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { PLAN_PRICES, SECTION_LEVEL, type Level } from '@fsp/core';
import { api, type Me } from './api';
import { money } from './form';
import { useDemo, useMinutesLeft } from './pages/demo';
import { errorText, PANEL_LOCALES, useI18n, type Locale } from './i18n';
import { Icon } from './icons';
import { ALL_SECTIONS, COMPANY_SECTION, FIRM_ONLY, IV_ONLY, locate, SECTIONS, SHOP_SECTION, visibleTabs, type NavSection, type NavTab } from './nav';

export function ThemeToggle() {
  const { t } = useI18n();
  const [light, setLight] = useState(() => document.documentElement.dataset.theme === 'light');
  const label = light ? t('Įjungti tamsų režimą') : t('Įjungti šviesų režimą');
  const toggle = () => {
    const next = light ? 'dark' : 'light';
    document.documentElement.dataset.theme = next;
    try { localStorage.setItem('fsp-theme', next); } catch { /* private mode */ }
    setLight(!light);
  };
  return (
    <button className="theme-toggle" type="button" onClick={toggle} aria-label={label} title={label} aria-pressed={light}>
      <span className="theme-icon theme-icon--sun"><Icon name="sun" /></span>
      <span className="theme-icon theme-icon--moon"><Icon name="moon" /></span>
    </button>
  );
}

/**
 * The language choice: in the top bar and on the sign-in card a small button with the current language that
 * opens the list (it took too much room as five buttons); in the phone's side menu all of them in a row.
 */
export function LanguageSwitch({ locales = PANEL_LOCALES, list = false }: { locales?: Partial<Record<Locale, string>>; list?: boolean }) {
  const { locale, setLocale } = useI18n();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (event: MouseEvent) => { if (!box.current?.contains(event.target as Node)) setOpen(false); };
    const esc = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);
  const entries = Object.entries(locales) as [Locale, string][];
  if (list) {
    return (
      <nav className="lang-switch" aria-label="Language">
        {entries.map(([code, name]) => (
          <a key={code} href={`#${code}`} hrefLang={code} title={name} className={code === locale ? 'active' : undefined} aria-current={code === locale ? 'true' : undefined}
            onClick={(event) => { event.preventDefault(); setLocale(code); }}>{code.toUpperCase()}</a>
        ))}
      </nav>
    );
  }
  return (
    <div className="lang-menu" ref={box}>
      <button type="button" className="lang-menu__button" aria-haspopup="listbox" aria-expanded={open} aria-label={`Language: ${locales[locale] ?? locale}`}
        title={locales[locale]} onClick={() => setOpen(!open)}>{locale.toUpperCase()}<span aria-hidden="true">▾</span></button>
      {open ? (
        <ul className="lang-menu__list" role="listbox" aria-label="Language">
          {entries.map(([code, name]) => (
            <li key={code} role="option" aria-selected={code === locale}>
              <button type="button" lang={code} className={code === locale ? 'active' : undefined} onClick={() => { setLocale(code); setOpen(false); }}>
                <b>{code.toUpperCase()}</b>{name}</button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** The sign-in / registration card of the PHP app. */
export function AuthCard({ eyebrow, title, intro, wide, children, footer }: {
  eyebrow: string; title: string; intro?: ReactNode; wide?: boolean; children: ReactNode; footer?: ReactNode;
}) {
  useEffect(() => { document.body.className = 'login-page'; return () => { document.body.className = ''; }; }, []);
  return (
    <main className="login-shell">
      <section className={wide ? 'login-card login-card--wide' : 'login-card'}>
        <div className="login-top">
          <Link className="login-mark" to="/login"><img src="media/favicon.png" alt="" width={34} height={34} /><span>FakturaSmart Pro</span></Link>
          <LanguageSwitch />
        </div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        {intro ? <p className="muted">{intro}</p> : null}
        {children}
        {footer ? <small>{footer}</small> : null}
      </section>
    </main>
  );
}

export function Alert({ kind, children }: { kind: 'success' | 'error'; children: ReactNode }) {
  return <div className={`alert alert--${kind}`} role={kind === 'error' ? 'alert' : 'status'}>{children}</div>;
}

/**
 * Back to where the person came from (the list with its tab, view and filters); opened directly (a link in
 * an e-mail, a new tab), to `fallback`.
 */
export function BackButton({ fallback, className = 'button button--ghost' }: { fallback: string; className?: string }) {
  const { t } = useI18n();
  const router = useRouter();
  const back = () => {
    // The router numbers its own history entries: above 0 there is a page of the app to go back to.
    const index = (window.history.state as { __TSR_index?: number } | null)?.__TSR_index ?? 0;
    if (index > 0) router.history.back(); else void router.navigate({ to: fallback });
  };
  return <button type="button" className={className} onClick={back}>← {t('Atgal')}</button>;
}

export function PageHead({ eyebrow, title, text, action, logo }: { eyebrow: string; title: string; text?: string; action?: ReactNode; logo?: string }) {
  return (
    <div className={`page-head${logo ? ' page-head--logo' : ''}`}>
      {logo ? <img className="page-head-logo" src={logo} alt="" /> : null}
      <div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1>{text ? <p>{text}</p> : null}</div>
      {action}
    </div>
  );
}

function useSidebar() {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('fsp-sidebar-collapsed') === '1'; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => { document.documentElement.classList.remove('sidebar-collapsed'); }, []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setMobileOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const toggleCollapsed = () => {
    setCollapsed((value) => {
      try { localStorage.setItem('fsp-sidebar-collapsed', value ? '0' : '1'); } catch { /* private mode */ }
      return !value;
    });
  };
  return { collapsed, toggleCollapsed, mobileOpen, setMobileOpen };
}

interface AiCredit {
  available: boolean;
  credit: { allowanceCents: number; usedMillicents: number; remainingMillicents: number; enabled: boolean };
  used: { invoices: number; statements: number; replies: number };
  topup: { cents: number; available: boolean };
}

const euro = (cents: number) => (cents / 100).toFixed(2).replace('.', ',');

/**
 * The month's AI credit in the top bar: used / available, amber from 80 %, red when under 0,10 € (AI reading
 * and the assistant stop until it is topped up or the month changes). Opens what it went to and the top-up.
 */
function AiMeter({ owner, premium }: { owner: boolean; premium: boolean }) {
  const { t } = useI18n();
  const data = useQuery({ queryKey: ['ai-credit'], queryFn: () => api.get<AiCredit>('api/v1/ai/credit'), staleTime: 60_000, refetchInterval: 300_000, enabled: premium });
  const topup = useMutation({ mutationFn: () => api.post<{ url: string }>('api/v1/ai/topup'), onSuccess: (r) => { window.location.assign(r.url); } });
  const d = data.data;
  // AI (the assistant and reading documents) is Premium's.
  if (!premium || !d?.available) return null;
  const used = Math.ceil(d.credit.usedMillicents / 1000);
  const state = d.credit.remainingMillicents < 10_000 ? 'is-out' : d.credit.usedMillicents >= d.credit.allowanceCents * 800 ? 'is-low' : '';
  // "+ Kreditas" (always there, coloured when the credit runs low): with the add-on, straight to the card
  // payment (5 €); without it, to the add-on in the Shop.
  const add = (className: string) => (owner && d.topup.available
    ? <button type="button" className={className} disabled={topup.isPending} onClick={() => topup.mutate()}>+ {t('Kreditas')}</button>
    : !d.credit.enabled ? <Link className={className} to="/shop" hash="ai-assistant">+ {t('Kreditas')}</Link> : null);
  return (
    <div className="ai-meter-wrap">
    {add(`ai-meter-add${state ? '' : ' is-quiet'}`)}
    <details className={`ai-meter ${state}`}>
      <summary title={t('AI kreditas šį mėnesį')}><Icon name="spark" /><span>{euro(used)} / {euro(d.credit.allowanceCents)} €</span></summary>
      <div className="ai-meter-panel">
        <strong>{t('AI kreditas šį mėnesį')}</strong>
        <p>{t('Išnaudota {used} € iš {all} €; liko {left} €.', { used: euro(used), all: euro(d.credit.allowanceCents), left: euro(Math.max(0, Math.floor(d.credit.remainingMillicents / 1000))) })}</p>
        <ul>
          <li>{t('Sąskaitos, perskaitytos su AI: {n}', { n: d.used.invoices })}</li>
          <li>{t('Banko išrašai, perskaityti su AI: {n}', { n: d.used.statements })}</li>
          {d.credit.enabled ? <li>{t('AI asistento atsakymai: {n}', { n: d.used.replies })}</li> : null}
        </ul>
        {state === 'is-out' ? <p className="ai-meter-out">{t('Kreditas baigėsi: dokumentai skaitomi be AI, asistentas neatsako, kol kreditas papildomas arba prasideda kitas mėnuo.')}</p> : null}
        {!d.credit.enabled ? <p className="muted">{t('AI asistentas išjungtas. Kreditas lieka dokumentų skaitymui.')} <Link to="/shop" hash="ai-assistant">{t('Į Parduotuvę')}</Link></p> : null}
        {owner && d.topup.available ? <button type="button" className="button button--small button--primary" disabled={topup.isPending} onClick={() => topup.mutate()}>{t('Papildyti {sum} €', { sum: euro(d.topup.cents) })}</button>
          : !d.credit.enabled ? <Link className="button button--small button--primary" to="/shop" hash="ai-assistant">{t('Įjungti AI asistentą')}</Link> : null}
        {topup.isError ? <p className="ai-meter-out">{errorText(t, topup.error)}</p> : null}
      </div>
    </details>
    </div>
  );
}

/** The Arkabi-style company panel: sidebar, top bar with the page title, content, footer. */
export function PanelShell({ me, head, children }: { me: Me; head: ReactNode; children: ReactNode }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useRouterState({ select: (s) => s.location });
  const path = location.pathname;
  const { collapsed, toggleCollapsed, mobileOpen, setMobileOpen } = useSidebar();
  useEffect(() => setMobileOpen(false), [path, setMobileOpen]);
  const logout = async () => {
    await api.post('api/v1/auth/logout').catch(() => undefined);
    queryClient.clear();
    await navigate({ to: '/login' });
  };
  // Another company of the same person: the session moves there and everything loads again.
  const switchCompany = async (companyId: number) => {
    await api.post('api/v1/auth/switch', { companyId });
    queryClient.clear();
    window.location.assign(document.baseURI);
  };
  const endView = async () => {
    await api.post('api/v1/auth/logout').catch(() => undefined);
    queryClient.clear();
    window.location.assign('admin?view=active');
  };
  // The demo shows the assistant's page (it explains the AI there) although the AI is off.
  const disabled = [...(me.disabledModules ?? []).filter((m) => !(me.demo && m === '/assistant')), ...(me.company.kind === 'iv' ? [] : IV_ONLY), ...(me.subscription?.plan === 'firm' ? [] : FIRM_ONLY)];
  const locked = me.subscription?.locked ?? [];
  const at = (tab: NavTab) => path === tab.to || path.startsWith(`${tab.to}/`);
  // A section the plan does not open: its address shows which plan has it (the data is kept).
  const lockedHere = me.viewAsAdmin ? undefined : ALL_SECTIONS.flatMap((s) => s.tabs).find((tab) => locked.includes(tab.to) && at(tab));
  // A page switched off in Parduotuvė: its address shows where to switch it back on (the data is kept).
  const off = ALL_SECTIONS.flatMap((s) => s.tabs).find((tab) => disabled.includes(tab.to) && at(tab));
  const here = locate(path, location.search as Record<string, unknown>, location.hash);
  // The router would mark every link to the same page active (Vartotojai and Nustatymai): `locate` decides.
  const tabLink = (tab: NavTab, active: boolean) => (
    <Link key={`${tab.to}|${tab.label}`} to={tab.to} search={(tab.search ?? {}) as never} hash={tab.hash ?? ''} activeProps={{}} className={active ? 'active' : undefined}>
      {t(tab.label)}{tab.soon ? <span className="section-soon">{t('Netrukus')}</span> : null}
      {locked.includes(tab.to) ? <span className="section-lock" title={t('Yra {plan} plane', { plan: LEVEL_NAMES[SECTION_LEVEL[tab.to]!] })}>{LEVEL_NAMES[SECTION_LEVEL[tab.to]!]}</span> : null}
    </Link>
  );
  const sectionLink = (section: NavSection) => {
    const first = visibleTabs(section, disabled)[0];
    if (!first) return null;
    return (
      <Link key={section.id} to={first.to} search={(first.search ?? {}) as never} hash={first.hash ?? ''} activeProps={{}} className={here?.section.id === section.id ? 'active' : undefined} title={t(section.label)}>
        <span className="nav-icon"><Icon name={section.icon} /></span><span className="nav-label">{t(section.label)}</span>
      </Link>
    );
  };
  const tabs = here ? visibleTabs(here.section, disabled) : [];
  const assistantOn = !disabled.includes('/assistant') && !locked.includes('/assistant') && path !== '/assistant';
  return (
    <div className={collapsed ? 'app-shell sidebar-collapsed' : 'app-shell'}>
      <aside className={mobileOpen ? 'sidebar is-open' : 'sidebar'}>
        <div className="sidebar-head">
          <Link className="panel-brand" to="/"><img src="media/favicon.png" alt="" width={28} height={28} /><span className="brand-text"><b>{me.company.name}</b><em>FakturaSmart Pro</em></span></Link>
          {(me.companies?.length ?? 0) > 1 ? (
            <select className="company-switch" aria-label={t('Keisti įmonę')} title={t('Keisti įmonę')} value={me.company.id} onChange={(e) => void switchCompany(Number(e.target.value))}>
              {me.companies!.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          ) : null}
          <button className="sidebar-collapse" type="button" onClick={toggleCollapsed} aria-expanded={!collapsed}
            aria-label={collapsed ? t('Išskleisti šoninį meniu') : t('Sutraukti šoninį meniu')} title={collapsed ? t('Išskleisti meniu') : t('Sutraukti meniu')}>
            <Icon name="chevron" />
          </button>
        </div>
        <nav>{SECTIONS.map(sectionLink)}</nav>
        <div className="sidebar-foot">
          {/* On phones the language choice lives here instead of the crowded top bar. */}
          <div className="sidebar-lang"><LanguageSwitch list /></div>
          <nav className="sidebar-account">{sectionLink(SHOP_SECTION)}{sectionLink(COMPANY_SECTION)}</nav>
          <div className="logout">
            <button type="button" onClick={logout} title={t('Atsijungti')}><span className="nav-icon"><Icon name="logout" /></span><span className="nav-label">{t('Atsijungti')}</span></button>
          </div>
        </div>
      </aside>
      <button type="button" className="sidebar-scrim" aria-label={t('Uždaryti meniu')} onClick={() => setMobileOpen(false)} />
      <main className="content">
        {me.viewAsAdmin ? (
          <div className="view-as-bar" role="status">
            <span><b>{t('Administratoriaus peržiūra')}</b> · {me.company.name} · {t('tik skaitymui, 1 val.')}</span>
            <button type="button" className="button button--small" onClick={endView}>{t('Baigti peržiūrą')}</button>
          </div>
        ) : null}
        {me.demo ? <DemoBar /> : null}
        {me.firm ? <FirmBar firm={me.firm} company={me.company.name} /> : null}
        {me.company.role === 'viewer' && !me.viewAsAdmin ? (
          <div className="view-as-bar view-as-bar--viewer" role="status"><span><b>{t('Tik peržiūra')}</b> · {me.company.name} · {t('galite matyti duomenis, bet nieko keisti negalite.')}</span></div>
        ) : null}
        {me.contract?.required && !['uploaded', 'accepted'].includes(me.contract.status) && !me.viewAsAdmin && me.subscription?.state !== 'expired' ? (
          <div className="plan-bar plan-bar--contract" role="status">
            <span>{me.contract.status === 'returned' ? t('Sutartis grąžinta pataisyti. Peržiūrėkite pastabą ir įkelkite iš naujo.') : t('Pasirašykite paslaugų sutartį su Arkabi (GoSign) ir įkelkite ją — tada galėsite užsisakyti planą.')}</span>
            <Link to="/account" hash="contract" className="button button--small">{t('Sutartis')}</Link>
          </div>
        ) : null}
        {me.subscription?.notice && !me.viewAsAdmin ? (
          <div className={`plan-bar plan-bar--${me.subscription.state}`} role="status">
            <span>{me.subscription.state === 'expired' ? (selfServe(me)
                ? t('Bandomasis laikotarpis baigėsi — dabar naudojate Nemokamą planą (iki 5 sąskaitų per mėnesį). Duomenys neištrinti: užsisakykite Basic arba Premium, kad visi skyriai vėl veiktų.')
                : t('Jūsų planas baigėsi. Parašykite mums, kad galėtumėte tęsti.'))
              : me.subscription.state === 'trial' ? t('Bandomasis laikotarpis baigiasi po {n} d.', { n: me.subscription.daysLeft ?? 0 })
              : t('Apmokėtas laikotarpis baigiasi po {n} d.', { n: me.subscription.daysLeft ?? 0 })}</span>
            {selfServe(me) && me.company.role === 'owner'
              ? <Link to="/account" hash="plan" className="button button--small">{t('Užsisakyti')}</Link>
              : <Link to="/support" className="button button--small">{t('Susisiekti')}</Link>}
          </div>
        ) : null}
        <header className="topbar">
          <button className="menu-toggle" type="button" aria-label={t('Meniu')} aria-expanded={mobileOpen} onClick={() => setMobileOpen(!mobileOpen)}>☰</button>
          {lockedHere ? <PageHead eyebrow={LEVEL_NAMES[SECTION_LEVEL[lockedHere.to]!]} title={t(lockedHere.label)} /> : head}
          <AiMeter owner={me.company.role === 'owner' && !me.viewAsAdmin} premium={me.subscription?.level === 'premium'} />
          <div className="workspace-status"><span className="status-dot" />{t('Saugi darbo erdvė')}</div>
          <LanguageSwitch />
          <ThemeToggle />
        </header>
        {tabs.length > 1 ? (
          <SectionTabs label={t(here!.section.label)} active={`${here!.tab?.to}|${here!.tab?.label}`}>{tabs.map((tab) => tabLink(tab, tab === here!.tab))}</SectionTabs>
        ) : null}
        {lockedHere ? <PlanLock me={me} name={t(lockedHere.label)} level={SECTION_LEVEL[lockedHere.to]!} /> : off ? (
          <section className="panel-card addon-off">
            <h2>{t('„{name}“ išjungta', { name: t(off.label) })}</h2>
            <p className="muted">{t('Šį priedą galite vėl įjungti Parduotuvėje. Duomenys neištrinti.')}</p>
            <Link to="/shop" className="button button--primary">{t('Į Parduotuvę')}</Link>
          </section>
        ) : children}
        {assistantOn ? <Link className="ai-fab" to="/assistant" title={t('AI asistentas')} aria-label={t('AI asistentas')}><Icon name="spark" /></Link> : null}
        <footer className="panel-footer">FakturaSmart Pro · {new Date().getFullYear()}</footer>
      </main>
    </div>
  );
}

/**
 * The right-hand column of a page (a form or upload box), closed by default as in the PHP panel: a thin
 * strip with "+" and the label that opens it, so the list gets the width. `open` opens it (e.g. editing).
 */
export function ThirdPane({ label, open: wanted = false, className = '', children }: { label: string; open?: boolean; className?: string; children: ReactNode }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(wanted);
  useEffect(() => { if (wanted) setOpen(true); }, [wanted]);
  const action = `${open ? t('Uždaryti') : t('Atidaryti')}: ${label}`;
  return (
    <aside className={`${className} third-pane${open ? ' third-pane-open' : ''}`}>
      <button type="button" className="third-pane-toggle" aria-expanded={open} aria-label={action} title={action} onClick={() => setOpen(!open)}>
        <span className="third-pane-toggle-icon" aria-hidden="true">{open ? '−' : '+'}</span>
        <span className="third-pane-toggle-label">{label}</span>
      </button>
      {children}
    </aside>
  );
}

export const LEVEL_NAMES: Record<Level, string> = { free: 'Nemokamas', basic: 'Basic', premium: 'Premium' };

/** Plans the company buys itself by card (not Pilna apskaita, which is by agreement, nor our own free companies). */
export const selfServe = (me: Me) => Boolean(me.billing?.enabled) && me.subscription?.plan !== 'full' && me.subscription?.priceCents !== 0;

/** In place of a section the plan does not open: which plan has it, and the way there. Nothing is deleted. */
export function PlanLock({ me, name, level }: { me: Me; name: string; level: Level }) {
  const { t } = useI18n();
  const owner = me.company.role === 'owner';
  return (
    <section className="panel-card addon-off plan-lock">
      <p className="eyebrow">{LEVEL_NAMES[level]}</p>
      <h2>{t('„{name}“ yra {plan} plane', { name, plan: LEVEL_NAMES[level] })}</h2>
      <p className="muted">{level === 'premium'
        ? t('Premium: bankas, kasa, balansai, mokesčiai, atlyginimai, sąskaitos el. paštu su AI ir viskas buhalteriui — {price} / mėn. + PVM.', { price: money(PLAN_PRICES.premium.month) })
        : t('Basic: neribotai sąskaitų, pasiūlymai, periodinės sąskaitos, sutartys ir darbai — {price} / mėn. + PVM.', { price: money(PLAN_PRICES.basic.month) })}</p>
      <p className="muted">{t('Jūsų duomenys neištrinti: įjungus planą viskas vėl matoma ir veikia.')}</p>
      <div className="form-actions">
        {owner && selfServe(me) ? <Link to="/account" hash="plan" className="button button--primary">{t('Pasirinkti planą')}</Link> : null}
        <Link to="/shop" className="button button--ghost">{t('Palyginti planus')}</Link>
        {!owner ? <span className="muted">{t('Planą keičia įmonės savininkas.')}</span> : null}
      </div>
    </section>
  );
}

/** On the demo: the data is made up and comes back every hour; another company is one click away. */
function DemoBar() {
  const { t } = useI18n();
  const demo = useDemo();
  const minutes = useMinutesLeft(demo.data?.resetsAt);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const other = async () => { await api.post('api/v1/auth/logout').catch(() => undefined); queryClient.clear(); await navigate({ to: '/login' }); };
  return (
    <div className="view-as-bar demo-bar" role="status">
      <span><b>{t('Demo')}</b> · {t('duomenys išgalvoti; viskas atkuriama po {n} min.', { n: minutes ?? 60 })}{demo.data ? ` · ${t('liko pakeitimų: {n}', { n: Math.max(0, demo.data.limits.writes - demo.data.limits.usedWrites) })}` : ''}</span>
      <button type="button" className="button button--small" onClick={() => void other()}>{t('Kita demo įmonė')}</button>
    </div>
  );
}

/** Working in a client's company through the accounting firm: whose company this is, and back to the board. */
function FirmBar({ firm, company }: { firm: { id: number; name: string }; company: string }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const back = async () => { await api.post('api/v1/auth/switch', { companyId: firm.id }); queryClient.clear(); await navigate({ to: '/firm' }); };
  return (
    <div className="view-as-bar firm-bar" role="status">
      <span><b>{company}</b> · {t('dirbate per {firm}', { firm: firm.name })}</span>
      <button type="button" className="button button--small" onClick={() => void back()}>← {t('Buhalterio panelė')}</button>
    </div>
  );
}

/** A section's tabs; on a phone they scroll sideways, so the open one is brought into view. */
function SectionTabs({ label, active, children }: { label: string; active: string; children: ReactNode }) {
  const nav = useRef<HTMLElement>(null);
  useEffect(() => {
    const box = nav.current;
    const tab = box?.querySelector<HTMLElement>('a.active');
    if (!box || !tab || box.scrollWidth <= box.clientWidth) return;
    const left = tab.getBoundingClientRect().left - box.getBoundingClientRect().left + box.scrollLeft;
    box.scrollLeft = Math.max(0, left - (box.clientWidth - tab.offsetWidth) / 2);
  }, [active]);
  return <nav ref={nav} className="project-tabs section-tabs" aria-label={label}>{children}</nav>;
}
